import { useEffect, useState } from "react";
import { Monitor, Trash2, Brain, KeyRound, Mic } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, fmtDate } from "@/lib/api";

const MODELS = {
  openai: ["gpt-5.4", "gpt-5.4-mini", "gpt-5.2", "gpt-4.1", "o4-mini"],
  anthropic: ["claude-sonnet-4-6", "claude-opus-4-6", "claude-haiku-4-5-20251001"],
  gemini: ["gemini-3.1-pro-preview", "gemini-3-flash-preview", "gemini-2.5-flash"],
};

export default function SettingsPage() {
  const [ai, setAi] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [voice, setVoice] = useState(null);
  const [pins, setPins] = useState({ current_pin: "", new_pin: "", confirm: "" });

  const load = () => {
    api.get("/agent/settings").then((r) => setAi(r.data)).catch(() => {});
    api.get("/auth/sessions").then((r) => setSessions(r.data)).catch(() => {});
    api.get("/voice/status").then((r) => setVoice(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const saveAi = async () => {
    try { await api.put("/agent/settings", { provider: ai.provider, model: ai.model, voice_reply: ai.voice_reply }); toast.success("تم حفظ إعدادات الذكاء"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async (id) => { await api.delete(`/auth/sessions/${id}`); load(); toast.success("تم إلغاء الجلسة"); };
  const changePin = async () => {
    if (pins.new_pin !== pins.confirm) return toast.error("الرمزان غير متطابقين");
    try { await api.post("/auth/change-pin", { current_pin: pins.current_pin, new_pin: pins.new_pin }); toast.success("تم تغيير الرمز السري"); setPins({ current_pin: "", new_pin: "", confirm: "" }); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="p-4 sm:p-8 space-y-6 max-w-5xl" data-testid="settings-page">
      <div>
        <p className="text-[11px] font-mono tracking-widest text-slate-500">الإدارة العامة</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-50">الإعدادات والجلسات</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="panel p-5 space-y-4" data-testid="ai-settings">
          <p className="font-semibold text-slate-100 flex items-center gap-2"><Brain size={16} className="text-cyan-300" /> نموذج الوكيل الذكي</p>
          {ai && (<>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs text-slate-400">المزود</Label>
                <select data-testid="ai-provider-select" value={ai.provider} onChange={(e) => setAi({ ...ai, provider: e.target.value, model: MODELS[e.target.value][0] })} className="dark-select">
                  <option value="openai">OpenAI</option><option value="anthropic">Anthropic</option><option value="gemini">Google Gemini</option>
                </select></div>
              <div className="space-y-1.5"><Label className="text-xs text-slate-400">النموذج</Label>
                <select data-testid="ai-model-select" value={ai.model} onChange={(e) => setAi({ ...ai, model: e.target.value })} className="dark-select" dir="ltr">
                  {(MODELS[ai.provider] || []).map((m) => <option key={m}>{m}</option>)}
                </select></div>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-white/[0.03] px-4 py-3">
              <div><p className="text-sm text-slate-200">مصدر المفتاح</p><p className="text-xs text-slate-500">{ai.key_source === "own" ? "مفتاحك الخاص من الخزنة" : ai.key_source === "env" ? "مفتاح المزود الخاص من إعدادات الخادم" : ai.key_source === "emergent" ? "المفتاح الموحد من إعدادات الخادم" : "لا يوجد مفتاح مفعّل لهذا المزود"}</p></div>
              <span data-testid="ai-key-source" className={`text-[11px] font-mono rounded-full px-2 py-0.5 ${ai.key_source === "missing" ? "bg-rose-500/10 text-rose-300" : "bg-emerald-500/10 text-emerald-300"}`}>{ai.key_source === "own" ? "OWN KEY" : ai.key_source === "env" ? "PROVIDER ENV" : ai.key_source === "emergent" ? "EMERGENT" : "MISSING"}</span>
            </div>
            <Button data-testid="ai-settings-save" onClick={saveAi} className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-semibold rounded-xl">حفظ</Button>
          </>)}
        </div>

        <div className="panel p-5 space-y-4" data-testid="voice-settings">
          <p className="font-semibold text-slate-100 flex items-center gap-2"><Mic size={16} className="text-purple-300" /> الصوت</p>
          <div className="flex items-center justify-between rounded-xl bg-white/[0.03] px-4 py-3">
            <div><p className="text-sm text-slate-200">التعرف الصوتي بالعربية</p><p className="text-xs text-slate-500">يعمل الآن عبر محرك المتصفح (Chrome/Safari) بدون مفتاح · Deepgram اختياري للدقة الأعلى</p></div>
            <span data-testid="voice-deepgram-status" className={`text-[11px] font-mono rounded-full px-2 py-0.5 ${voice?.deepgram ? "bg-emerald-500/10 text-emerald-300" : "bg-cyan-400/10 text-cyan-300"}`}>{voice?.deepgram ? "Deepgram" : "المتصفح"}</span>
          </div>
          <div className="flex items-center justify-between rounded-xl bg-white/[0.03] px-4 py-3">
            <div><p className="text-sm text-slate-200">القراءة الصوتية للرد</p><p className="text-xs text-slate-500">عبر محرك المتصفح (عربي)</p></div>
            {ai && <Switch data-testid="voice-reply-switch" checked={ai.voice_reply} onCheckedChange={(v) => setAi({ ...ai, voice_reply: v })} />}
          </div>
        </div>

        <div className="panel p-5 space-y-3" data-testid="sessions-panel">
          <p className="font-semibold text-slate-100 flex items-center gap-2"><Monitor size={16} className="text-emerald-300" /> الجلسات النشطة (كل متصفح مستقل)</p>
          {sessions.map((s) => (
            <div key={s.id} data-testid={`session-${s.id}`} className="flex items-center justify-between rounded-xl bg-white/[0.03] px-4 py-3">
              <div>
                <p className="text-sm text-slate-200">{s.device} {s.current && <span className="text-[10px] font-mono text-cyan-300 mr-2">الحالية</span>}</p>
                <p className="text-xs text-slate-500">{s.ip} · آخر نشاط {fmtDate(s.last_seen)}</p>
              </div>
              {!s.current && <button data-testid={`session-revoke-${s.id}`} onClick={() => revoke(s.id)} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-red-300 hover:bg-red-500/10"><Trash2 size={14} /></button>}
            </div>
          ))}
        </div>

        <div className="panel p-5 space-y-3" data-testid="pin-panel">
          <p className="font-semibold text-slate-100 flex items-center gap-2"><KeyRound size={16} className="text-amber-300" /> تغيير الرمز السري</p>
          <Input data-testid="pin-current-input" type="password" inputMode="numeric" placeholder="الرمز الحالي" value={pins.current_pin} onChange={(e) => setPins({ ...pins, current_pin: e.target.value })} className="bg-slate-900 border-slate-800" />
          <Input data-testid="pin-new-input" type="password" inputMode="numeric" placeholder="الرمز الجديد (4-12 رقم)" value={pins.new_pin} onChange={(e) => setPins({ ...pins, new_pin: e.target.value })} className="bg-slate-900 border-slate-800" />
          <Input data-testid="pin-confirm-input" type="password" inputMode="numeric" placeholder="تأكيد الرمز الجديد" value={pins.confirm} onChange={(e) => setPins({ ...pins, confirm: e.target.value })} className="bg-slate-900 border-slate-800" />
          <Button data-testid="pin-change-button" onClick={changePin} className="bg-amber-400 text-slate-950 hover:bg-amber-300 font-semibold rounded-xl">تغيير الرمز</Button>
        </div>
      </div>
    </div>
  );
}
